import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import RestockWorklist, { ProductLines } from './RestockWorklist';
import MerchandisingBoard from './MerchandisingBoard';
import OfficeReturnSection from './OfficeReturnSection';
import RestockDemandSummary from './RestockDemandSummary';
import DailyReplenishment from './DailyReplenishment';
import OutletTransferDialog from './OutletTransferDialog';
import { newReference, OutletCandidate } from './merchandisingTypes';
import SeasonalPlanningPanel from './SeasonalPlanningPanel';
import DemandFeedback from './DemandFeedback';
import AutumnTransitionSummary from './AutumnTransitionSummary';
import StoreInventorySection from './StoreInventorySection';
import InventoryCountEditor, { pendingInventoryIds } from './InventoryCountEditor';
import { actionListText, groupOrderLines, officeReturnListText, officeReturnPieces, pieces, readNokeError, removalListText, RestockPlan, reviewVariantLabel, variantLabel } from './restockPlan';

const Fold: React.FC<{ title: string; children: React.ReactNode; id?: string; expanded?: boolean; onToggle?: (expanded: boolean) => void }> = ({ title, children, id, expanded, onToggle }) => (
  <Accordion id={id} expanded={expanded} onChange={onToggle ? (_, value) => onToggle(value) : undefined} disableGutters sx={{ mb: 1, scrollMarginTop: 24 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>{title}</Typography></AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const OptionalFold: React.FC<{ folded: boolean; title: string; children: React.ReactNode; expanded?: boolean; onToggle?: (expanded: boolean) => void }> = ({ folded, children, ...props }) => folded ? <Fold {...props}>{children}</Fold> : <>{children}</>;

const RestockTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [plan, setPlan] = useState<RestockPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [coverDays, setCoverDays] = useState<number | undefined>();
  const [capacityInput, setCapacityInput] = useState('');
  const [capacity, setCapacity] = useState<number | undefined>();
  const [copyText, setCopyText] = useState<string | null>(null);
  const [showProposed, setShowProposed] = useState(false);
  const [historical, setHistorical] = useState(false);
  const [editing, setEditing] = useState(false);
  const [trialLimit, setTrialLimit] = useState(3);
  const [trialLimitInput, setTrialLimitInput] = useState('3');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dailyOutlet, setDailyOutlet] = useState<OutletCandidate | null>(null);
  const [planningDirty, setPlanningDirty] = useState(false);
  const [planningReady, setPlanningReady] = useState(false);
  const [inventoryEditors, setInventoryEditors] = useState<Record<string, boolean>>({});
  const [recoveringInventoryIds, setRecoveringInventoryIds] = useState(pendingInventoryIds);
  const inventoryLocked = Object.values(inventoryEditors).some(Boolean);
  const onInventoryEditingChange = useCallback((key: string, value: boolean) => setInventoryEditors((old) => old[key] === value ? old : { ...old, [key]: value }), []);
  const [demandLocked, setDemandLocked] = useState(false);
  const [visitRef, setVisitRef] = useState(() => {
    try { return sessionStorage.getItem('noke-merchandising-visit') || newReference('visit'); } catch { return newReference('visit'); }
  });
  useEffect(() => { try { sessionStorage.setItem('noke-merchandising-visit', visitRef); } catch { /* current visitor stays in memory */ } }, [visitRef]);
  const nextVisitor = useCallback(() => {
    if (demandLocked) return;
    setVisitRef(newReference('visit')); enqueueSnackbar('已切換下一位客人', { variant: 'info' });
  }, [demandLocked, enqueueSnackbar]);
  const refreshAsProposed = useRef(false);
  const requestId = useRef(0);

  const fetchPlan = useCallback(async (proposed = false) => {
    const id = ++requestId.current;
    setLoading(true);
    setPlan(null);
    setCopyText(null);
    setError('');
    setHistorical(false);
    setShowProposed(false);
    setEditing(false);
    setPlanningDirty(false);
    setDetailsOpen(proposed);
    setDailyOutlet(null);
    try {
      const response = await api.get<RestockPlan>('/api/noke/restock', {
        params: { ...(coverDays === undefined ? {} : { cover_days: coverDays }), max_trial_skus: trialLimit, ...(capacity === undefined ? {} : { capacity_pieces: capacity }) },
      });
      if (requestId.current === id) { setPlan(response.data); setShowProposed(proposed); }
    } catch (err: any) {
      if (requestId.current === id) setError(readNokeError(err, '無法更新補貨建議，請重試。'));
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, [coverDays, capacity, trialLimit]);

  useEffect(() => {
    fetchPlan(refreshAsProposed.current);
    refreshAsProposed.current = false;
    return () => { requestId.current += 1; };
  }, [fetchPlan]);

  const refreshInventory = useCallback(async (): Promise<boolean> => {
    const id = ++requestId.current;
    try {
      const { data } = await api.get<RestockPlan>('/api/noke/restock', { params: { ...(coverDays === undefined ? {} : { cover_days: coverDays }), max_trial_skus: trialLimit, ...(capacity === undefined ? {} : { capacity_pieces: capacity }) } });
      if (id !== requestId.current) return false;
      setPlan(data); setError('');
      enqueueSnackbar('庫存已儲存，補貨建議已更新。', { variant: 'success' });
      return true;
    } catch { return false; }
    finally { if (id === requestId.current) setLoading(false); }
  }, [coverDays, trialLimit, capacity, enqueueSnackbar]);

  const copy = async (text: string) => {
    if (!plan || loading || inventoryLocked) return;
    const id = requestId.current;
    try {
      await navigator.clipboard.writeText(text);
      if (id === requestId.current) enqueueSnackbar('已複製清單', { variant: 'success' });
    } catch {
      if (id === requestId.current) setCopyText(text);
    }
  };

  const capacityValid = capacityInput === '' || (/^\d+$/.test(capacityInput) && Number(capacityInput) > 0 && Number.isSafeInteger(Number(capacityInput)));
  const trialLimitValid = /^\d+$/.test(trialLimitInput) && Number(trialLimitInput) >= 0 && Number(trialLimitInput) <= 20;
  const space = plan?.meta.space;
  const weather = plan?.meta.weather;
  const waiting = plan?.waiting || [];
  const officeReturnSkus = new Set((plan?.office_returns || []).map((row) => row.sku));
  const review = (plan?.review || []).filter((row) => !(row.review_type === 'excess_depth' && officeReturnSkus.has(row.sku)));
  const deferred = plan?.deferred || [];
  const dailyMode = !!(plan?.daily_actions && plan.merchandising);
  const stockFirst = (plan?.meta.strategy || plan?.merchandising?.strategy)?.mode === 'store_core_stock_rotation';
  const horizon = plan?.planning_horizon || plan?.merchandising?.planning_horizon;
  const autumn = plan?.meta.autumn_transition || plan?.merchandising?.autumn_transition;
  const updatePlanning = () => {
    if (coverDays === undefined) fetchPlan(true);
    else { refreshAsProposed.current = true; setCoverDays(undefined); }
  };

  return (
    <Box>
      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="h1">{dailyMode ? '今日門市補貨與換款' : plan?.merchandising ? '門市陳列與補貨' : '今日補貨建議'}</Typography>
            <Typography variant="body2" color="text.secondary">{dailyMode ? '先補齊主力色碼，再用現有庫存換款。' : plan?.merchandising ? '先看每架放什麼，再依明細補齊顏色、尺寸與件數。' : '依序準備上架、調貨與訂貨；每列已列出要拿的顏色、尺寸和件數。'}</Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => fetchPlan()} disabled={loading || editing || planningDirty || demandLocked || inventoryLocked}>更新建議</Button>
        </Box>
        {plan && space && !historical && !showProposed && (
          <>
            {!dailyMode && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 4 }, my: 2 }}>
              {[
                { label: '門市上架', count: pieces(plan.display || []), href: '#noke-restock-step-1' },
                { label: '辦公室調貨', count: pieces(plan.dispatch), href: '#noke-restock-step-2' },
                { label: '需要訂貨', count: pieces(plan.order), href: '#noke-restock-step-3' },
                { label: '在途等待', count: pieces(waiting), href: waiting.length ? '#noke-restock-waiting' : undefined },
                ...(plan.office_returns ? [{ label: '撤回評估', count: officeReturnPieces(plan.office_returns), href: '#noke-office-return' }] : []),
              ].map(({ label, count, href }) => (
                <Box component="a" href={href} key={label} aria-label={href ? `${label} ${count} 件，跳至清單` : undefined}
                  sx={{ color: href ? 'primary.main' : 'text.primary', textDecoration: 'none', borderRadius: 0.5, '&:hover': { textDecoration: href ? 'underline' : 'none' } }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h6">{count} <Typography component="span" variant="body2">件</Typography></Typography>
                </Box>
              ))}
            </Box>}
            {dailyMode ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>衣架現有 {space.rail_now} 件／設定或估計容量 {space.assumed_capacity} 件；可先備貨，實際補入前仍需安排空間。</Typography> : <>
            <Typography variant="body2" color="text.secondary">
              衣架現有 {space.rail_now} 件 · 衣架在途 {space.incoming === undefined ? '待確認' : `${space.incoming} 件`} · 衣架新增 {space.add} 件{space.pull > 0 ? ` · 計畫撤回 ${space.pull} 件` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              執行後估計 {space.end_state} 件／{plan.meta.selection?.capacity_source === 'configured' ? '設定容量' : '估計容量'} {space.assumed_capacity} 件；實際擺放仍需核對衣架空間。
            </Typography>
            <Typography variant="caption" color="text.secondary">衣架容量僅計服飾，配件與 MdoC 另計。</Typography>
            </>}
            {space.pull > 0 && <Alert severity="warning" sx={{ mt: 1.5 }}>本方案需先按下方「建議撤回」騰出 {space.pull} 件空間，再放入新貨。</Alert>}
            {space.over_by > 0 && <Alert severity="warning" sx={{ mt: 1.5 }}>仍超出容量 {space.over_by} 件。請先確認可撤回商品與實際容量，再安排新增商品。</Alert>}
            {!dailyMode && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {weather?.status === 'available' ? `${weather.label}：${weather.summary}` : '天氣預報未取得：本次僅使用季節資料。'}
            </Typography>}
            <Typography variant="caption" color="text.secondary">資料更新：{new Date(plan.meta.generated_at).toLocaleString('zh-TW')}</Typography>
            {!dailyMode && <RestockDemandSummary history={plan.meta.demand_history} season={plan.meta.seasonal_advice} />}
          </>
        )}
      </Paper>

      {!historical && recoveringInventoryIds.map((id) => <InventoryCountEditor key={id} variantId={id} label={`色碼 #${id}`} recovery onInventoryUpdated={async () => {
        const ok = await refreshInventory(); if (ok) setRecoveringInventoryIds((ids) => ids.filter((value) => value !== id)); return ok;
      }} onDismiss={() => setRecoveringInventoryIds((ids) => ids.filter((value) => value !== id))} onInventoryEditingChange={onInventoryEditingChange} />)}
      {inventoryLocked && <Typography role="status" variant="body2" sx={{ mb: 1 }}>先完成庫存儲存或取消編輯，再更新或採用配置。</Typography>}
      {horizon && !loading && !historical && <SeasonalPlanningPanel horizon={horizon} disabled={editing || inventoryLocked} onUpdated={updatePlanning} onEditingChange={setPlanningDirty} onReadyChange={setPlanningReady} />}
      {autumn?.enabled && !loading && !historical && <AutumnTransitionSummary summary={autumn} proposed={showProposed} />}
      {plan?.store_inventory_review && !loading && !historical && <StoreInventorySection review={plan.store_inventory_review} autumn={autumn?.enabled} onCopy={copy}
        onInventoryUpdated={refreshInventory} onInventoryEditingChange={onInventoryEditingChange} inventoryDisabled={inventoryLocked || editing || planningDirty} recoveringInventoryIds={recoveringInventoryIds} />}
      {horizon && plan?.merchandising && !loading && !historical && <DemandFeedback key={visitRef} visitRef={visitRef} enabled={plan.merchandising.schema_ready && planningReady && !inventoryLocked} onNextVisitor={nextVisitor} onLockedChange={setDemandLocked} />}
      {plan?.daily_actions && plan.merchandising && !loading && !historical && !showProposed && <DailyReplenishment
        actions={plan.daily_actions} strategy={plan.meta.strategy || plan.merchandising.strategy} racks={plan.merchandising.racks}
        seasonalPlanning={!!horizon}
        strategyDisabled={planningDirty || demandLocked || inventoryLocked}
        onInventoryUpdated={refreshInventory} onInventoryEditingChange={onInventoryEditingChange} inventoryDisabled={inventoryLocked || editing || planningDirty} recoveringInventoryIds={recoveringInventoryIds}
        hasActivePlan={!!plan.merchandising.active_plan} outletCandidates={plan.merchandising.outlet_candidates}
        canConfirmOutlet={plan.merchandising.schema_ready && !editing && !planningDirty && !inventoryLocked} onCopy={copy} onStrategy={() => fetchPlan(true)} onOutlet={setDailyOutlet} />}

      <Fold title="調整備貨天數與容量">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          {!horizon && <TextField select label="備貨天數" size="small" disabled={editing || planningDirty || inventoryLocked} value={coverDays ?? plan?.meta.cover_days ?? 14} onChange={(event) => setCoverDays(Number(event.target.value))} sx={{ minWidth: 130 }}
            helperText={plan?.meta.demand_history ? '控制要備幾天的貨；銷售分析期間另見「本次選品依據」。' : '這個天數只控制預計備貨量。'}>
            {[7, 14, 21, 30].map((days) => <MenuItem key={days} value={days}>{days} 天</MenuItem>)}
          </TextField>}
          {!plan?.merchandising && <TextField label="可展示件數" size="small" disabled={editing || inventoryLocked} value={capacityInput} onChange={(event) => setCapacityInput(event.target.value)}
            error={!capacityValid} helperText={capacityValid ? '留白採用估計容量；依實際衣架可放件數設定。' : '請輸入大於 0 的整數'} inputProps={{ inputMode: 'numeric' }} />
          }
          {!plan?.merchandising && <Button variant="outlined" disabled={!capacityValid || loading || inventoryLocked} onClick={() => setCapacity(capacityInput === '' ? undefined : Number(capacityInput))}>套用容量</Button>}
          {plan?.merchandising && <Typography variant="body2" color="text.secondary">各架容量與主題請由下方「調整衣架」設定。</Typography>}
          {plan?.merchandising && !stockFirst && <TextField type="number" size="small" label="自動試賣款數上限" disabled={inventoryLocked || editing || loading} value={trialLimitInput} onChange={(event) => setTrialLimitInput(event.target.value)}
            inputProps={{ min: 0, max: 20, step: 1 }} error={!trialLimitValid} helperText="0–20 款；只更新新建議，正式配置需另行採用。" />}
          {plan?.merchandising && !stockFirst && <Button variant="outlined" disabled={inventoryLocked || editing || loading || !trialLimitValid || Number(trialLimitInput) === trialLimit} onClick={() => { refreshAsProposed.current = true; setTrialLimit(Number(trialLimitInput)); }}>套用試賣上限</Button>}
          {editing && <Typography variant="body2" color="text.secondary">請先儲存或捨棄手動調整，再更新條件。</Typography>}
        </Box>
      </Fold>

      {loading && <Box role="status" sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /><Typography sx={{ mt: 1 }}>正在重新計算補貨清單…</Typography></Box>}
      {error && <Alert severity="error" sx={{ my: 2 }}> {error} 舊清單已暫停使用。</Alert>}

      {plan && !loading && (
        <>
          {!historical && !showProposed && !plan.store_inventory_review && plan.office_returns && <OptionalFold folded={dailyMode} title="撤回辦公室與 Outlet 評估"><OfficeReturnSection rows={plan.office_returns} onCopy={() => copy(officeReturnListText(plan.office_returns || []))} /></OptionalFold>}
          {plan.merchandising && <OptionalFold folded={dailyMode} title="衣架配置、連結、歷史與換款分析" expanded={detailsOpen || historical || showProposed || editing || (!!horizon && !plan.merchandising.active_plan)} onToggle={setDetailsOpen}>
            {dailyMode && <><Typography variant="caption" color="text.secondary">{weather?.status === 'available' ? `${weather.label}：${weather.summary}` : '天氣預報未取得：本次僅使用季節資料。'}</Typography><RestockDemandSummary history={plan.meta.demand_history} season={plan.meta.seasonal_advice} /></>}
            <MerchandisingBoard data={{ ...plan.merchandising, autumn_transition: autumn, planning_horizon: horizon, proposed_worklist: plan.proposed_worklist || plan.merchandising.proposed_worklist }} overCapacity={plan.meta.space.over_by > 0} onRefresh={fetchPlan}
              visitRef={visitRef} onNextVisitor={nextVisitor} visitLocked={demandLocked} onCopy={copy}
              planningLocked={planningDirty || inventoryLocked} seasonalPlanningReady={!horizon || planningReady}
              initialShowProposed={showProposed} onHistoryChange={setHistorical} onProposedChange={setShowProposed} onEditingChange={setEditing} orders={plan.order} evidenceSnapshot={plan.meta} generatedAt={plan.meta.generated_at} />
          </OptionalFold>}
          {!historical && showProposed && <Alert severity="info">採用這版後，會重新產生對應的上架、調貨與訂貨清單。</Alert>}
          {!historical && !showProposed && <>
          <OptionalFold folded={dailyMode} title="即時可執行補貨明細與等待項目">
          {plan.merchandising && <Typography component="h2" variant="h6" sx={{ mt: 3, mb: 2 }}>依陳列補齊商品</Typography>}
          <RestockWorklist step={1} title="先把門市現有貨上架" note="使用門市備用庫存，依下列位置擺放；不需要再訂。"
            empty="目前沒有需要從門市備用庫存補上架的商品。" lines={plan.display || []} plan={plan} copyLabel="複製上架清單" onCopy={() => copy(actionListText(plan, 'display'))} />
          <RestockWorklist step={2} title="從辦公室拿這些" note="按清單揀貨；送出與門市收貨請到「庫存」頁登記。"
            empty="目前沒有需要從辦公室調入的商品。" lines={plan.dispatch} plan={plan} copyLabel="複製調貨清單" onCopy={() => copy(actionListText(plan, 'dispatch'))} />
          <RestockWorklist step={3} title="向供應商訂這些" note="韓國訂貨約一週內到店。未登錄的韓國訂單不在計算內，複製前核對是否已訂；複製清單不會下單或記錄已訂。"
            empty="目前沒有需要新增訂貨的商品。" lines={plan.order} plan={plan} copyLabel="複製待訂貨清單" onCopy={() => copy(actionListText(plan, 'order'))}>
            {groupOrderLines(plan.order).map((group) => (
              <Box key={group.key} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ pb: 1 }}>{group.source} · {pieces(group.lines)} 件</Typography>
                <ProductLines lines={group.lines} plan={plan} />
              </Box>
            ))}
          </RestockWorklist>

          {plan.pull.length > 0 && <Fold title={`建議撤回 · ${space?.pull || 0} 件`}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>撤回門市陳列，商品仍可在線上販售；實際移動請至庫存頁登記。</Typography>
            <Button size="small" onClick={() => copy(removalListText(plan))}>複製撤回清單</Button>
            {plan.pull.map((row) => <Box key={row.sku} sx={{ py: 1.5 }}>
              <Typography fontWeight={600}>{row.sku} · {row.title}</Typography>
              <Typography variant="body2">{row.variants.filter((variant) => variant.on_hand > 0).map((variant) => `${variantLabel(variant.color, variant.size)} × ${variant.on_hand} 件`).join('、')}</Typography>
              <Typography variant="body2" color="text.secondary">{row.reason}</Typography>
            </Box>)}
          </Fold>}
          {review.length > 0 && <Fold title={`需要檢查陳列的商品 · ${new Set(review.map((row) => row.sku)).size} 款`}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>這些是檢查候選。先確認實際展示、缺碼與試穿情況，再決定撤回；尚未計入可騰出的空間。</Typography>
            {review.map((row, index) => <Box key={`${row.sku}:${row.review_type || index}`} sx={{ py: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>{row.sku} · {row.title} · 現有 {row.on_hand} 件</Typography>
              <Typography variant="body2" color="text.secondary">{row.reason}</Typography>
              {row.variants.map((variant) => <Typography key={variant.variant_id} variant="body2" sx={{ mt: 0.5 }}>{reviewVariantLabel(variant)}</Typography>)}
            </Box>)}
          </Fold>}
          {waiting.length > 0 && <Fold id="noke-restock-waiting" title={`在途等待 · ${pieces(waiting)} 件`}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>以下貨品已在途，先等待收貨。數量已納入本次建議。</Typography>
            <ProductLines lines={waiting} plan={plan} />
          </Fold>}
          {deferred.length > 0 && <Fold title={`暫不加入本次清單 · ${deferred.length} 筆`}>
            {deferred.map((row, index) => <Box key={`${row.sku}:${index}`} sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={600}>{row.sku} · {row.title} · {row.qty} 件</Typography>
              <Typography variant="body2" color="text.secondary">{row.reason}</Typography>
            </Box>)}
          </Fold>}
          {plan.blocked.length > 0 && <Fold title={`目前無法補貨 · ${plan.blocked.length} 款`}>
            {plan.blocked.map((row) => <Box key={row.sku} sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={600}>{row.sku} · {row.title}</Typography>
              <Typography variant="body2" color="text.secondary">{row.reason}</Typography>
            </Box>)}
          </Fold>}
          {!!plan.meta.selection?.notes.length && <Fold title="本次推薦依據與資料限制">
            {plan.meta.selection.notes.map((note) => <Typography key={note} variant="body2" color="text.secondary" sx={{ mb: 1 }}>{note}</Typography>)}
          </Fold>}
          </OptionalFold>
          </>}
        </>
      )}

      <Dialog open={copyText !== null} onClose={() => setCopyText(null)} maxWidth="sm" fullWidth>
        <DialogTitle>請手動複製清單</DialogTitle>
        <DialogContent><TextField multiline fullWidth minRows={10} value={copyText || ''} inputProps={{ 'aria-label': '可複製的建議清單', readOnly: true }} /></DialogContent>
        <DialogActions><Button onClick={() => setCopyText(null)}>關閉</Button></DialogActions>
      </Dialog>
      {dailyOutlet && plan?.merchandising && <OutletTransferDialog item={dailyOutlet} enabled={plan.merchandising.schema_ready && !editing && !loading}
        onClose={() => setDailyOutlet(null)} onSaved={() => { enqueueSnackbar('已調入辦公室待送；實際送櫃與收貨請到庫存頁登記。', { variant: 'success' }); fetchPlan(); }} />}
    </Box>
  );
};

export default RestockTab;
